import { useEffect, useMemo, type RefObject } from "react";

/**
 * Touch scrolling for the guide.
 *
 * The guide's rows scroll vertically on ONE element, the thin scroll rail (see
 * EpgScrollRail): the channel list and the programme grid are virtualized from
 * its scrollTop and are overflow:hidden themselves, so they never drift apart.
 * A mouse wheel over them is forwarded to the rail, but a finger had no such
 * path — on a phone the only way to move the guide was the 12px rail itself.
 *
 * This forwards vertical finger drags over the channels and the grid to the
 * rail, with a fling on release like a native list. Horizontal drags are left
 * to the browser: the grid scrolls sideways natively (the containers set
 * `touch-action: pan-x`, which also stops a vertical drag from scrolling the
 * page or triggering pull-to-refresh). The direction locks after a few pixels,
 * so a slightly diagonal swipe moves one way, not both.
 */

export interface VerticalScroller {
  scrollTop: number;
}

interface TouchPoint {
  clientX: number;
  clientY: number;
}

export interface TouchLike {
  touches: ArrayLike<TouchPoint>;
}

export interface ClickLike {
  preventDefault(): void;
  stopPropagation(): void;
}

export interface TouchScrollClock {
  now: () => number;
  requestFrame: (callback: () => void) => number;
  cancelFrame: (id: number) => void;
}

/** Movement before the drag commits to an axis. Below it, a touch is a tap. */
const AXIS_LOCK_PX = 8;
/** Only the last stretch of the drag decides the fling speed — a finger that
 *  stopped before lifting should not fling. */
const VELOCITY_WINDOW_MS = 100;
/** Slower releases (px/ms) just stop where the finger left the guide. */
const MIN_FLING_SPEED = 0.15;
const STOP_SPEED = 0.02;
/** Exponential decay time constant — the feel of a native Android/iOS list. */
const FLING_TIME_CONSTANT_MS = 325;
/** A drag that ends on a channel or programme must not also tap it. */
const CLICK_GUARD_MS = 400;

export interface TouchScrollHandlers {
  onTouchStart: (event: TouchLike) => void;
  onTouchMove: (event: TouchLike) => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
  onClickCapture: (event: ClickLike) => void;
  /** Stop any fling in progress (unmount). */
  stop: () => void;
}

const browserClock: TouchScrollClock = {
  now: () => performance.now(),
  requestFrame: (callback) => requestAnimationFrame(callback),
  cancelFrame: (id) => cancelAnimationFrame(id),
};

export function createTouchScroll(
  getScroller: () => VerticalScroller | null,
  clock: TouchScrollClock = browserClock,
): TouchScrollHandlers {
  let startX = 0;
  let startY = 0;
  let lastY = 0;
  let tracking = false;
  let axis: "x" | "y" | null = null;
  let samples: { t: number; y: number }[] = [];
  let frame: number | null = null;
  let dragEndedAt = -Infinity;

  const stop = () => {
    if (frame !== null) clock.cancelFrame(frame);
    frame = null;
  };

  const fling = (initialVelocity: number) => {
    let velocity = initialVelocity;
    let last = clock.now();
    const step = () => {
      frame = null;
      const scroller = getScroller();
      if (!scroller) return;
      const t = clock.now();
      const dt = t - last;
      last = t;
      const before = scroller.scrollTop;
      scroller.scrollTop = before - velocity * dt;
      velocity *= Math.exp(-dt / FLING_TIME_CONSTANT_MS);
      // The element clamps scrollTop; no movement means an end of the list.
      const hitEdge = dt > 0 && scroller.scrollTop === before;
      if (Math.abs(velocity) >= STOP_SPEED && !hitEdge) frame = clock.requestFrame(step);
    };
    frame = clock.requestFrame(step);
  };

  const release = () => {
    if (tracking && axis === "y") {
      dragEndedAt = clock.now();
      const recent = samples.filter((s) => dragEndedAt - s.t <= VELOCITY_WINDOW_MS);
      if (recent.length >= 2) {
        const first = recent[0]!;
        const last = recent[recent.length - 1]!;
        const velocity = (last.y - first.y) / Math.max(last.t - first.t, 1);
        if (Math.abs(velocity) >= MIN_FLING_SPEED) fling(velocity);
      }
    }
    tracking = false;
    axis = null;
    samples = [];
  };

  return {
    onTouchStart: (event) => {
      stop(); // a touch catches a fling, as in any native list
      if (event.touches.length !== 1) {
        tracking = false; // pinch or multi-finger: not ours
        return;
      }
      const touch = event.touches[0]!;
      tracking = true;
      axis = null;
      startX = touch.clientX;
      startY = lastY = touch.clientY;
      samples = [{ t: clock.now(), y: touch.clientY }];
    },
    onTouchMove: (event) => {
      if (!tracking || event.touches.length !== 1) return;
      const touch = event.touches[0]!;
      if (axis === null) {
        const dx = Math.abs(touch.clientX - startX);
        const dy = Math.abs(touch.clientY - startY);
        if (Math.max(dx, dy) < AXIS_LOCK_PX) return;
        axis = dy > dx ? "y" : "x";
        // Scroll from where the lock happened, not from the start point: the
        // first few pixels were tap tolerance, and jumping them feels jerky.
        lastY = touch.clientY;
      }
      if (axis !== "y") return;
      const scroller = getScroller();
      if (scroller) scroller.scrollTop -= touch.clientY - lastY;
      lastY = touch.clientY;
      const t = clock.now();
      samples.push({ t, y: touch.clientY });
      if (samples.length > 1 && t - samples[0]!.t > VELOCITY_WINDOW_MS * 2) samples.shift();
    },
    onTouchEnd: release,
    onTouchCancel: release,
    onClickCapture: (event) => {
      if (clock.now() - dragEndedAt > CLICK_GUARD_MS) return;
      dragEndedAt = -Infinity;
      event.preventDefault();
      event.stopPropagation();
    },
    stop,
  };
}

/** React binding: spread the returned handlers on a guide container. */
export function useGuideTouchScroll(scrollRef: RefObject<HTMLElement | null>) {
  const handlers = useMemo(() => createTouchScroll(() => scrollRef.current), [scrollRef]);
  useEffect(() => handlers.stop, [handlers]);
  return {
    onTouchStart: handlers.onTouchStart,
    onTouchMove: handlers.onTouchMove,
    onTouchEnd: handlers.onTouchEnd,
    onTouchCancel: handlers.onTouchCancel,
    onClickCapture: handlers.onClickCapture,
  };
}
